import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MobileComparisonView = ({ selectedCranes, onRemoveCrane }) => {
  const [currentCraneIndex, setCurrentCraneIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    valuation: false,
    performance: false,
    market: false
  });

  const specificationSections = [
    {
      id: 'basic',
      title: 'Basic Specifications',
      icon: 'Settings',
      specs: [
        { label: 'Make & Model', key: 'makeModel' },
        { label: 'Year', key: 'year' },
        { label: 'Capacity', key: 'capacity' },
        { label: 'Operating Hours', key: 'hours', format: 'number' },
        { label: 'Location', key: 'location' }
      ]
    },
    {
      id: 'valuation',
      title: 'Valuation & Pricing',
      icon: 'DollarSign',
      specs: [
        { label: 'Current Price', key: 'price' },
        { label: 'Market Value Range', key: 'valueRange' },
        { label: 'Confidence Score', key: 'confidenceScore' },
        { label: 'Days on Market', key: 'daysOnMarket' },
        { label: 'Depreciation Rate', key: 'depreciationRate' }
      ]
    },
    {
      id: 'performance',
      title: 'Performance',
      icon: 'Zap',
      specs: [
        { label: 'Boom Length', key: 'boomLength' },
        { label: 'Lift Height', key: 'liftHeight' },
        { label: 'Working Radius', key: 'workingRadius' },
        { label: 'Engine Power', key: 'enginePower' },
        { label: 'Fuel Consumption', key: 'fuelConsumption' }
      ]
    },
    {
      id: 'market',
      title: 'Market Data',
      icon: 'TrendingUp',
      specs: [
        { label: 'Available Units', key: 'availableUnits' },
        { label: 'Regional Demand', key: 'regionalDemand' },
        { label: 'Comparable Sales', key: 'comparableSales' },
        { label: 'Seasonal Variation', key: 'seasonalVariation' }
      ]
    }
  ];

  // Enhanced crane data for mobile view
  const enhancedCraneData = selectedCranes?.map(crane => ({
    ...crane,
    makeModel: `${crane?.make} ${crane?.model}`,
    valueRange: crane?.make === 'Liebherr' ? '$780K - $920K' : crane?.make === 'Grove' ? '$850K - $990K' : '$720K - $840K',
    confidenceScore: crane?.make === 'Liebherr' ? '92%' : crane?.make === 'Grove' ? '88%' : '85%',
    daysOnMarket: crane?.make === 'Liebherr' ? '45 days' : crane?.make === 'Grove' ? '38 days' : '52 days',
    depreciationRate: crane?.make === 'Liebherr' ? '8.5%/year' : crane?.make === 'Grove' ? '9.2%/year' : '7.8%/year',
    boomLength: crane?.make === 'Liebherr' ? '84m' : crane?.make === 'Grove' ? '78m' : '68m',
    liftHeight: crane?.make === 'Liebherr' ? '84m' : crane?.make === 'Grove' ? '78m' : '68m',
    workingRadius: crane?.make === 'Liebherr' ? '68m' : crane?.make === 'Grove' ? '62m' : '58m',
    enginePower: crane?.make === 'Liebherr' ? '367 kW' : crane?.make === 'Grove' ? '390 kW' : '300 kW',
    fuelConsumption: crane?.make === 'Liebherr' ? '28 L/h' : crane?.make === 'Grove' ? '32 L/h' : '26 L/h',
    availableUnits: crane?.make === 'Liebherr' ? '12 units' : crane?.make === 'Grove' ? '8 units' : '15 units',
    regionalDemand: crane?.make === 'Liebherr' ? 'High' : crane?.make === 'Grove' ? 'Medium' : 'Medium',
    comparableSales: crane?.make === 'Liebherr' ? '8 sales' : crane?.make === 'Grove' ? '6 sales' : '9 sales',
    seasonalVariation: crane?.make === 'Liebherr' ? '12%' : crane?.make === 'Grove' ? '15%' : '10%'
  }));

  const currentCrane = enhancedCraneData?.[currentCraneIndex];

  const nextCrane = () => {
    setCurrentCraneIndex((prev) => (prev + 1) % selectedCranes?.length);
  };

  const prevCrane = () => {
    setCurrentCraneIndex((prev) => (prev - 1 + selectedCranes?.length) % selectedCranes?.length);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const formatValue = (value, format) => {
    if (!value) return 'N/A';
    
    switch (format) {
      case 'number':
        return typeof value === 'number' ? value?.toLocaleString() : value;
      default:
        return value;
    }
  };

  if (selectedCranes?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center">
          <Icon name="Smartphone" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
          <h3 className="text-lg font-medium text-text-primary mb-2">Mobile Comparison View</h3>
          <p className="text-text-secondary">
            Select cranes to view detailed comparisons optimized for mobile devices.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header with Navigation */}
      <div className="bg-muted/30 p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-text-primary">Mobile Comparison</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemoveCrane(currentCrane?.id)}
            iconName="X"
            className="text-text-secondary hover:text-destructive"
          />
        </div>
        
        {/* Crane Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={prevCrane}
            iconName="ChevronLeft"
            disabled={selectedCranes?.length <= 1}
          />
          
          <div className="text-center flex-1">
            <h3 className="font-medium text-text-primary">
              {currentCrane?.make} {currentCrane?.model}
            </h3>
            <p className="text-sm text-text-secondary">
              {currentCraneIndex + 1} of {selectedCranes?.length}
            </p>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={nextCrane}
            iconName="ChevronRight"
            disabled={selectedCranes?.length <= 1}
          />
        </div>

        {/* Crane Indicators */}
        <div className="flex justify-center space-x-2 mt-3">
          {selectedCranes?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentCraneIndex(index)}
              className={`w-2 h-2 rounded-full transition-smooth ${
                index === currentCraneIndex ? 'bg-primary' : 'bg-border'
              }`}
            />
          ))}
        </div>
      </div>
      {/* Crane Image */}
      <div className="p-4">
        <div className="w-full h-48 bg-muted rounded-lg overflow-hidden mb-4">
          <img
            src={currentCrane?.image}
            alt={currentCrane?.imageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Price Badge */}
        <div className="flex items-center justify-center mb-4">
          <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full">
            <span className="font-semibold text-lg">{currentCrane?.price}</span>
          </div>
        </div>
      </div>
      {/* Specification Sections */}
      <div className="pb-4">
        {specificationSections?.map((section) => (
          <div key={section?.id} className="border-b border-border last:border-b-0">
            <button
              onClick={() => toggleSection(section?.id)}
              className="w-full px-4 py-4 flex items-center justify-between hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-center space-x-3">
                <Icon name={section?.icon} size={18} className="text-primary" />
                <span className="font-medium text-text-primary">{section?.title}</span>
              </div>
              <Icon 
                name={expandedSections?.[section?.id] ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-text-secondary" 
              />
            </button>
            
            {expandedSections?.[section?.id] && (
              <div className="px-4 pb-4">
                <div className="space-y-3">
                  {section?.specs?.map((spec) => (
                    <div key={spec?.key} className="flex justify-between items-center py-2">
                      <span className="text-sm text-text-secondary">{spec?.label}</span>
                      <span className="text-sm font-medium text-text-primary">
                        {formatValue(currentCrane?.[spec?.key], spec?.format)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Quick Actions */}
      <div className="p-4 bg-muted/30 border-t border-border">
        <div className="grid grid-cols-2 gap-3">
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            iconPosition="left"
          >
            View Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Share2"
            iconPosition="left"
          >
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MobileComparisonView;